import { FC } from 'react';
import KpiCard from '@/components/admin/KpiCard';
import { fmtMedida, type KpisVendas as Kpis, type Medida } from '@/utils/equiplexVendas';
import { fmtData, fmtPct, fmtUn } from '@/utils/painelFormat';

interface Props {
  kpis: Kpis;
  medida: Medida;
  corte: string;
}

function subFaturado(k: Kpis): string {
  if (k.futuro) return 'o mês ainda não começou';
  if (k.pctDoForecast === null) return 'sem forecast para comparar';
  return `${fmtPct(k.pctDoForecast)} do forecast do mês`;
}

function subRitmo(k: Kpis, medida: Medida, corte: string): string {
  if (k.futuro) return 'o mês ainda não começou';
  if (k.esperado === null) return 'sem forecast no mês';
  const ate = k.emAndamento ? `até ${fmtData(corte).slice(0, 5)}` : 'no mês';
  return `esperado ${ate}: ${fmtMedida(k.esperado, medida)}`;
}

/** No mês com forecast: quantos SKUs do forecast já faturaram; sem forecast, só quantos venderam. */
function skus(k: Kpis): { valor: string; rotulo: string; sub: string } {
  if (k.futuro) return { valor: '—', rotulo: 'SKUs vendidos', sub: 'o mês ainda não começou' };
  if (k.forecast === null) return { valor: fmtUn(k.skusFaturados), rotulo: 'SKUs vendidos', sub: 'com nota no mês' };
  const fora = k.skusForaDoForecast;
  return {
    valor: `${fmtUn(k.skusForecastVendidos)} de ${fmtUn(k.skusForecast)}`,
    rotulo: 'SKUs do forecast vendidos',
    sub: fora ? `+${fmtUn(fora)} vendidos fora do forecast` : 'nenhuma venda fora do forecast',
  };
}

function subUf(k: Kpis): string {
  if (!k.ufPrincipal) return 'nenhuma nota no mês';
  return `principal: ${k.ufPrincipal.nome} (${fmtPct(k.ufPrincipal.share)})`;
}

/**
 * Os números do mês escolhido na medida da tela (unidades ou R$). O ritmo compara o
 * faturado com o forecast proporcional aos dias que já passaram — é ele que diz se o mês fecha.
 */
const KpisVendas: FC<Props> = ({ kpis, medida, corte }) => {
  const s = skus(kpis);
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(15rem,1fr))] gap-4" data-testid="vendas-kpis">
      <KpiCard
        icon="flag"
        value={fmtMedida(kpis.forecast, medida)}
        label="Forecast do mês"
        sublabel={kpis.forecast === null ? 'mês sem forecast no Balanceamento' : `${fmtUn(kpis.skusForecast)} SKUs no forecast`}
      />
      <KpiCard icon="receipt_long" value={fmtMedida(kpis.faturado, medida)} label="Faturado" sublabel={subFaturado(kpis)} />
      <KpiCard icon="speed" value={fmtPct(kpis.aderenciaRitmo)} label="Aderência ao ritmo" sublabel={subRitmo(kpis, medida, corte)} />
      <KpiCard icon="inventory_2" value={s.valor} label={s.rotulo} sublabel={s.sub} />
      <KpiCard icon="map" value={kpis.futuro ? '—' : fmtUn(kpis.ufs)} label="UFs atendidas" sublabel={subUf(kpis)} />
    </div>
  );
};

export default KpisVendas;
